import { supabase } from '../../config/supabase';
import { SystemConfiguration, AppVersionDetail } from '../../types/cms.types';

export interface SystemConfigFilter {
    search?: string;
    app_name?: string;
}

export interface SystemConfigListResponse {
    data: SystemConfiguration[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface CreateSystemConfigRequest {
    app_name: string;
    version_details: AppVersionDetail[];
    terms_of_service_url?: string;
    privacy_policy_url?: string;
    support_email?: string;
    support_phone?: string;
    support_fanpage?: string;
    support_zalo?: string;
    support_group_facebook?: string;
    company_name?: string;
    company_address?: string;
}

export interface UpdateSystemConfigRequest extends CreateSystemConfigRequest {
    id: string;
}

export class SystemConfigurationService {
    /**
     * Lấy danh sách cấu hình hệ thống với phân trang và filter
     */
    async getSystemConfigurations(
        page: number = 1,
        limit: number = 10,
        filters: SystemConfigFilter = {}
    ): Promise<SystemConfigListResponse> {
        try {
            const offset = (page - 1) * limit;

            let query = supabase
                .from('system_configurations')
                .select(`
                    *,
                    app_version_details(*)
                `, { count: 'exact' });

            // Apply filters
            if (filters.app_name) {
                query = query.ilike('app_name', `%${filters.app_name}%`);
            }

            if (filters.search) {
                query = query.or(`app_name.ilike.%${filters.search}%,company_name.ilike.%${filters.search}%`);
            }

            // Apply pagination and ordering
            query = query
                .range(offset, offset + limit - 1)
                .order('created_at', { ascending: false });

            const { data, error, count } = await query;

            if (error) {
                throw new Error(`Lỗi khi tải danh sách cấu hình: ${error.message}`);
            }

            // Transform data to match expected format
            const transformedData = data?.map(item => ({
                ...item,
                version_details: item.app_version_details || []
            })) || [];

            const totalPages = Math.ceil((count || 0) / limit);

            return {
                data: transformedData,
                total: count || 0,
                page,
                limit,
                totalPages
            };
        } catch (error) {
            console.error('Error in getSystemConfigurations:', error);
            throw error;
        }
    }

    /**
     * Lấy thông tin chi tiết cấu hình hệ thống
     */
    async getSystemConfigurationById(id: string): Promise<SystemConfiguration | null> {
        try {
            const { data, error } = await supabase
                .from('system_configurations')
                .select(`
                    *,
                    app_version_details(*)
                `)
                .eq('id', id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    return null;
                }
                throw new Error(`Lỗi khi tải thông tin cấu hình: ${error.message}`);
            }

            // Transform data to match expected format
            const transformedData = {
                ...data,
                version_details: data.app_version_details || []
            };

            return transformedData;
        } catch (error) {
            console.error('Error in getSystemConfigurationById:', error);
            throw error;
        }
    }

    /**
     * Lấy cấu hình hệ thống hiện tại (active)
     */
    async getCurrentSystemConfiguration(): Promise<SystemConfiguration | null> {
        try {
            const { data, error } = await supabase
                .from('system_configurations')
                .select(`
                    *,
                    app_version_details(*)
                `)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    return null;
                }
                throw new Error(`Lỗi khi tải cấu hình hiện tại: ${error.message}`);
            }

            // Transform data to match expected format
            const transformedData = {
                ...data,
                version_details: data.app_version_details || []
            };

            return transformedData;
        } catch (error) {
            console.error('Error in getCurrentSystemConfiguration:', error);
            throw error;
        }
    }

    /**
     * Tạo cấu hình hệ thống mới
     */
    async createSystemConfiguration(request: CreateSystemConfigRequest): Promise<SystemConfiguration> {
        try {
            // Extract version_details from request
            const { version_details, ...systemConfigData } = request;

            // Create system configuration first
            const { data: systemConfig, error: systemConfigError } = await supabase
                .from('system_configurations')
                .insert([{
                    ...systemConfigData,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                }])
                .select()
                .single();

            if (systemConfigError) {
                throw new Error(`Lỗi khi tạo cấu hình: ${systemConfigError.message}`);
            }

            // Insert version details if provided
            if (version_details && version_details.length > 0) {
                const versionDetailsToInsert = version_details.map(vd => ({
                    ...vd,
                    system_config_id: systemConfig.id,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                }));

                const { error: versionError } = await supabase
                    .from('app_version_details')
                    .insert(versionDetailsToInsert);

                if (versionError) {
                    // Rollback system config if version details fail
                    await supabase.from('system_configurations').delete().eq('id', systemConfig.id);
                    throw new Error(`Lỗi khi tạo chi tiết phiên bản: ${versionError.message}`);
                }
            }

            // Return full system configuration with version details
            return await this.getSystemConfigurationById(systemConfig.id) as SystemConfiguration;
        } catch (error) {
            console.error('Error in createSystemConfiguration:', error);
            throw error;
        }
    }

    /**
     * Cập nhật cấu hình hệ thống
     */
    async updateSystemConfiguration(request: UpdateSystemConfigRequest): Promise<SystemConfiguration> {
        try {
            // Extract version_details from request
            const { version_details, id, ...systemConfigData } = request;

            // Update system configuration
            const { error: systemConfigError } = await supabase
                .from('system_configurations')
                .update({
                    ...systemConfigData,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id)
                .select()
                .single();

            if (systemConfigError) {
                throw new Error(`Lỗi khi cập nhật cấu hình: ${systemConfigError.message}`);
            }

            // Handle version details update if provided
            if (version_details) {
                // Delete existing version details
                await supabase
                    .from('app_version_details')
                    .delete()
                    .eq('system_config_id', id);

                // Insert new version details
                if (version_details.length > 0) {
                    const versionDetailsToInsert = version_details.map(vd => {
                        // Remove id, created_at, updated_at from the version detail object
                        const { id: vdId, created_at, updated_at, ...versionDetailData } = vd as any;
                        return {
                            ...versionDetailData,
                            system_config_id: id,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString()
                        };
                    });

                    const { error: versionError } = await supabase
                        .from('app_version_details')
                        .insert(versionDetailsToInsert);

                    if (versionError) {
                        throw new Error(`Lỗi khi cập nhật chi tiết phiên bản: ${versionError.message}`);
                    }
                }
            }

            // Return full system configuration with version details
            return await this.getSystemConfigurationById(id) as SystemConfiguration;
        } catch (error) {
            console.error('Error in updateSystemConfiguration:', error);
            throw error;
        }
    }

    /**
     * Xóa cấu hình hệ thống
     */
    async deleteSystemConfiguration(id: string): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('system_configurations')
                .delete()
                .eq('id', id);

            if (error) {
                throw new Error(`Lỗi khi xóa cấu hình: ${error.message}`);
            }

            return true;
        } catch (error) {
            console.error('Error in deleteSystemConfiguration:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách version details theo system config id
     */
    async getVersionDetails(systemConfigId: string): Promise<AppVersionDetail[]> {
        try {
            const { data, error } = await supabase
                .from('app_version_details')
                .select('*')
                .eq('system_config_id', systemConfigId)
                .order('created_at', { ascending: false });

            if (error) {
                throw new Error(`Lỗi khi tải chi tiết phiên bản: ${error.message}`);
            }

            return data || [];
        } catch (error) {
            console.error('Error in getVersionDetails:', error);
            throw error;
        }
    }

    /**
     * Tạo version detail mới
     */
    async createVersionDetail(systemConfigId: string, versionDetail: Omit<AppVersionDetail, 'id' | 'created_at' | 'updated_at'>): Promise<AppVersionDetail> {
        try {
            const { data, error } = await supabase
                .from('app_version_details')
                .insert([{
                    ...versionDetail,
                    system_config_id: systemConfigId,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                }])
                .select()
                .single();

            if (error) {
                throw new Error(`Lỗi khi tạo chi tiết phiên bản: ${error.message}`);
            }

            return data;
        } catch (error) {
            console.error('Error in createVersionDetail:', error);
            throw error;
        }
    }

    /**
     * Cập nhật version detail
     */
    async updateVersionDetail(id: string, versionDetail: Partial<AppVersionDetail>): Promise<AppVersionDetail> {
        try {
            const { data, error } = await supabase
                .from('app_version_details')
                .update({
                    ...versionDetail,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id)
                .select()
                .single();

            if (error) {
                throw new Error(`Lỗi khi cập nhật chi tiết phiên bản: ${error.message}`);
            }

            return data;
        } catch (error) {
            console.error('Error in updateVersionDetail:', error);
            throw error;
        }
    }

    /**
     * Xóa version detail
     */
    async deleteVersionDetail(id: string): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('app_version_details')
                .delete()
                .eq('id', id);

            if (error) {
                throw new Error(`Lỗi khi xóa chi tiết phiên bản: ${error.message}`);
            }

            return true;
        } catch (error) {
            console.error('Error in deleteVersionDetail:', error);
            throw error;
        }
    }

    /**
     * Lấy phiên bản mới nhất theo OS
     */
    async getLatestVersionByOS(osType: 'ios' | 'android' | 'web'): Promise<AppVersionDetail | null> {
        try {
            // Tìm system config active trước
            const currentConfig = await this.getCurrentSystemConfiguration();
            if (!currentConfig) return null;

            const { data, error } = await supabase
                .from('app_version_details')
                .select('*')
                .eq('system_config_id', currentConfig.id)
                .eq('os_type', osType)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    return null;
                }
                throw new Error(`Lỗi khi tải phiên bản mới nhất: ${error.message}`);
            }

            return data;
        } catch (error) {
            console.error('Error in getLatestVersionByOS:', error);
            throw error;
        }
    }

    // /**
    //  * Kiểm tra có cần force update không
    //  */
    // async checkForceUpdate(osType: 'ios' | 'android' | 'web', currentVersion: string): Promise<boolean> {
    //     try {
    //         const latestVersion = await this.getLatestVersionByOS(osType);
    //         if (!latestVersion) return false;

    //         // So sánh version và check force_update
    //         return latestVersion.force_update === true && latestVersion.version !== currentVersion;
    //     } catch (error) {
    //         console.error('Error in checkForceUpdate:', error);
    //         return false;
    //     }
    // }
}